import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FamilyChoice } from '@couch/shared';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { groupModels, type FolderRows, type ModelRow as Row } from './model-groups';
import { ModelRow, type RowActions } from './ModelRow';
import { useDeleteModel } from './useDeleteModel';
import { LIBRARY_UPLOADS_KEY, libraryApi } from '../../api/storage';
import { useStorageConnected } from '../connections/useStorageConnected';

/** Which ComfyUI node reads each folder — a full SDXL checkpoint is not a "diffusion model". */
const LOADER: Readonly<Record<string, string>> = Object.freeze({
  checkpoints: 'Load Checkpoint', loras: 'Load LoRA', vae: 'Load VAE', upscale_models: 'Load Upscale Model',
  embeddings: 'the prompt (embedding:name)', controlnet: 'Load ControlNet Model', clip_vision: 'Load CLIP Vision',
  diffusion_models: 'Load Diffusion Model', text_encoders: 'Load CLIP', style_models: 'Load Style Model',
  latent_upscale_models: 'Load Latent Upscale Model', model_patches: 'Model Patch Loader', audio_encoders: 'Audio Encoder Loader',
  hypernetworks: 'Load Hypernetwork', photomaker: 'Load PhotoMaker', gligen: 'GLIGEN Loader',
});

function Group({ title, groupId, folders, missing = [], intro, actions }: {
  title: string; groupId: FamilyChoice | null; folders: FolderRows; missing?: string[]; intro?: string; actions: RowActions;
}) {
  return (
    <div className="model-family">
      <h3>{title}</h3>
      {intro && <p className="muted">{intro}</p>}
      {missing.length > 0 && <p role="alert" className="form-error">Missing: {missing.join(', ')}. The model cannot run without it.</p>}
      {folders.map(([folder, rows]) => (
        <div key={folder} className="model-folder">
          <h4>{folder} {LOADER[folder] ? <span className="muted">— load with “{LOADER[folder]}”</span> : <span className="muted">— read by its own nodes</span>}</h4>
          <ul>{rows.map((row) => <ModelRow key={`${row.folder}/${row.file.name}`} row={row} groupId={groupId} actions={actions} />)}</ul>
        </div>
      ))}
    </div>
  );
}

function useSetFamily(machineId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ row, family }: { row: Row; family: FamilyChoice | null }) => api.setModelFamily(machineId, { folder: row.folder, name: row.file.name, family }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['models', machineId] }),
  });
}

/** "To library" (doc 75): offered when a storage key is reachable — on Couch or on a home machine. */
function useToLibrary(machineId: string) {
  const queryClient = useQueryClient();
  const connected = useStorageConnected();
  const send = useMutation({
    mutationFn: (row: Row) => libraryApi.toLibrary({ machineId, folder: row.folder, fileName: row.file.name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIBRARY_UPLOADS_KEY }),
  });
  return { connected, send };
}

/** Installed models grouped by model family (doc 48). */
export function ModelsPanel({ machineId }: { machineId: string }) {
  const models = useQuery({ queryKey: ['models', machineId], queryFn: () => api.machineModels(machineId), retry: false });
  const { ask, done, remove } = useDeleteModel(machineId);
  const family = useSetFamily(machineId);
  const library = useToLibrary(machineId);
  const actions: RowActions = {
    onToLibrary: library.connected ? (row) => { library.send.mutate(row); } : undefined,
    onDelete: (row) => { ask({ folder: row.folder, name: row.file.name, label: row.file.name, sizeBytes: row.file.sizeBytes }); },
    onFamily: (row, choice) => { family.mutate({ row, family: choice }); },
    busy: remove.isPending || family.isPending || library.send.isPending,
  };
  const groups = models.data ? groupModels(models.data) : null;
  const empty = groups && Object.values(models.data?.folders ?? {}).every((files) => files.length === 0);
  return (
    <section className="panel" aria-labelledby="models-title">
      <h2 id="models-title">Installed models</h2>
      <p className="muted">Grouped by model family: LoRAs, VAEs and text encoders only work with the base models of their own family.</p>
      {models.isPending && <p role="status">Loading…</p>}
      {models.isError && <p role="alert" className="form-error">{errorMessage(models.error)}</p>}
      {empty && <p className="muted">No models yet — install one above.</p>}
      {done && <p role="status">{done}</p>}
      {remove.isError && <p role="alert" className="form-error">{errorMessage(remove.error)}</p>}
      {family.isError && <p role="alert" className="form-error">{errorMessage(family.error)}</p>}
      {library.send.isSuccess && <p role="status">Sending {library.send.data.upload.fileName} to your library — follow it in <Link to="/library">Library</Link>.</p>}
      {library.send.isError && <p role="alert" className="form-error">{errorMessage(library.send.error)}</p>}
      {groups?.families.map((g) => (
        <Group key={g.family.id} title={g.hasBase ? g.family.label : `${g.family.label} — parts only, no base model`} groupId={g.family.id}
          folders={g.folders} missing={g.missing} actions={actions} />
      ))}
      {groups && groups.leftover.length > 0 && <Group title="Parts without their base model" groupId={null} folders={groups.leftover} actions={actions} />}
      {groups && groups.any.length > 0 && <Group title="Works with any model" groupId={null} folders={groups.any} actions={actions} />}
      {groups && groups.unsure.length > 0 && (
        <Group title="Not sure which model" groupId={null} folders={groups.unsure} actions={actions}
          intro="Couch could not tell which model these belong to. Choose one, and Couch remembers it for this machine." />
      )}
    </section>
  );
}
